import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { articles } from '@/mocks/article';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16 flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-semibold text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/articles" className="text-primary-500 hover:text-primary-600 text-sm cursor-pointer">返回文章列表</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles.filter((a) => a.slug !== slug).slice(0, 3);

  const articleSchema = {
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: { '@type': 'Person', name: article.author },
    url: `https://www.moguwang.com/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: '蘑菇网',
      url: 'https://www.moguwang.com',
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 蘑菇网穿搭灵感`}
        description={article.excerpt}
        keywords={`蘑菇网,${article.category},${article.tags.join(',')}`}
        canonicalUrl={`https://www.moguwang.com/article/${article.slug}`}
        ogImage={article.image}
        ogType="article"
        schema={[articleSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '穿搭灵感', to: '/articles' },
              { label: article.title },
            ]}
          />

          <article className="w-full py-8 md:py-12">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="text-xs font-medium text-accent-600 bg-accent-100 px-3 py-1 rounded-full">{article.category}</span>
                  <span className="text-xs text-foreground-400">{article.date}</span>
                  <span className="text-xs text-foreground-400">{article.author}</span>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-4">{article.title}</h1>
                <p className="text-base text-foreground-600 leading-relaxed">{article.excerpt}</p>
              </div>

              <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10">
                <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-semibold
                  prose-p:text-foreground-700 prose-p:leading-relaxed
                  prose-strong:text-foreground-900 prose-strong:font-semibold
                  prose-a:text-primary-500 prose-a:no-underline hover:prose-a:text-primary-600
                  prose-li:text-foreground-700
                  [&_h2]:text-xl [&_h2]:mt-8 [&_h2]:mb-4
                  [&_h3]:text-lg [&_h3]:mt-6 [&_h3]:mb-3
                  [&_p]:mb-4
                "
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-8 flex items-center gap-2 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">相关文章</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group cursor-pointer"
                    >
                      <div className="aspect-[16/10] rounded-lg overflow-hidden mb-3">
                        <img src={ra.image} alt={ra.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      </div>
                      <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">{ra.title}</h4>
                      <p className="text-xs text-foreground-400 mt-1">{ra.date}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </article>
        </div>

        <Footer />
      </div>
    </>
  );
}